import { AxiosResponse } from 'axios';
import { ResponseWithPagination } from '@/types';
import { PTOHoursEntry, PTORequest, PTOStatus, PTOType, PTOTypeBalance } from '@/types/api';
import { makeDateWithCurrentTime } from '@/utils/dates';
import { sortEntriesAscending } from '@/utils/pto';
import { supabase } from '@/lib/supabase';
import { asResponse, paginationRange, buildPaginatedResponse } from '@/lib/supabase-helpers';

export type CreatePTOBody = {
  firstDay: Date;
  lastDay: Date;
  hours: number[];
  requestDate: Date;
  ptoType: PTOType;
  userSlackId?: string;
  userId?: string;
  userEmail?: string;
  requestComment?: string;
};

const mapStatusFromDb = (status: string): PTOStatus => {
  const statusMap: Record<string, PTOStatus> = {
    pending: PTOStatus.PENDING,
    approved: PTOStatus.APPROVED,
    denied: PTOStatus.DENIED,
  };
  return statusMap[status] ?? PTOStatus.PENDING;
};

const mapStatusToDb = (status: PTOStatus): string => {
  const statusMap: Record<PTOStatus, string> = {
    [PTOStatus.PENDING]: 'pending',
    [PTOStatus.APPROVED]: 'approved',
    [PTOStatus.DENIED]: 'denied',
  };
  return statusMap[status];
};

const mapPtoTypeFromDb = (type: string): PTOType => {
  return type as PTOType;
};

// Build entries from pto_hours_entries rows
const buildEntries = (hoursEntries: any[]): PTOHoursEntry[] => {
  return (hoursEntries || []).map((e: any) => ({
    id: e.id,
    date: makeDateWithCurrentTime(e.date),
    hours: Number(e.hours),
  }));
};

const parsePTORow = (row: any, hoursEntries?: any[]): PTORequest => {
  const entries = buildEntries(hoursEntries || row.pto_hours_entries || []);

  return {
    id: row.id,
    staffMemberId: row.profile_id,
    ptoType: mapPtoTypeFromDb(row.pto_type),
    status: mapStatusFromDb(row.status),
    firstDay: makeDateWithCurrentTime(row.first_day),
    entries: entries.sort(sortEntriesAscending),
    requestDate: new Date(row.request_date),
    requestComment: row.request_comment ?? undefined,
    decisionDate: row.decision_date ? new Date(row.decision_date) : null,
    decisionComment: row.decision_comment ?? null,
    updatedAt: new Date(row.updated_at),
    answeredBy: null as any,
    answeredById: row.answered_by_id || '',
  };
};

const createPTORequest = async (body: CreatePTOBody): Promise<AxiosResponse<PTORequest>> => {
  // Create the PTO request
  const { data, error } = await supabase
    .from('pto_requests')
    .insert({
      profile_id: body.userId,
      pto_type: body.ptoType,
      status: 'pending',
      first_day: body.firstDay.toISOString().split('T')[0],
      request_date: body.requestDate.toISOString(),
      request_comment: body.requestComment ?? null,
    })
    .select()
    .single();

  if (error) throw error;

  // Create hour entries for each day
  const entries: { pto_request_id: string; date: string; hours: number }[] = [];
  const current = new Date(body.firstDay);
  for (let i = 0; i < body.hours.length; i++) {
    entries.push({
      pto_request_id: data.id,
      date: new Date(current).toISOString().split('T')[0],
      hours: body.hours[i],
    });
    current.setDate(current.getDate() + 1);
  }

  if (entries.length > 0) {
    const { error: entryError } = await supabase
      .from('pto_hours_entries')
      .insert(entries);
    if (entryError) throw entryError;
  }

  // Fetch back with entries
  const { data: full } = await supabase
    .from('pto_requests')
    .select('*, pto_hours_entries(*)')
    .eq('id', data.id)
    .single();

  return asResponse(parsePTORow(full || data));
};

type GetPTOsByStaffMemberOptions = {
  staffMemberId: string;
  page: number;
  take: number;
  filter?: PTOStatus;
  order?: 'ASC' | 'DESC';
};

const getPTOsByStaffMember = async ({
  staffMemberId,
  page,
  take,
  filter,
  order,
}: GetPTOsByStaffMemberOptions): Promise<AxiosResponse<ResponseWithPagination<PTORequest[]>>> => {
  let query = supabase
    .from('pto_requests')
    .select('*, pto_hours_entries(*)', { count: 'exact' })
    .eq('profile_id', staffMemberId);

  if (filter) {
    query = query.eq('status', mapStatusToDb(filter));
  }

  const orderDirection = order === 'ASC' ? true : false;
  query = query.order('first_day', { ascending: orderDirection });

  const [from, to] = paginationRange(page, take);
  query = query.range(from, to);

  const { data, error, count } = await query;

  if (error) throw error;

  const totalCount = count ?? 0;
  const ptoRequests = (data || []).map((r: any) => parsePTORow(r));

  return asResponse<ResponseWithPagination<PTORequest[]>>(buildPaginatedResponse(ptoRequests, totalCount, page, take) as any);
};

export type BankOfHoursBalanceResponse = Array<PTOTypeBalance>;

const getBankOfHoursBalance = async ({
  staffMemberId,
}: {
  staffMemberId: string;
}): Promise<AxiosResponse<BankOfHoursBalanceResponse>> => {
  const { data, error } = await supabase
    .from('bank_of_hours')
    .select('*')
    .eq('profile_id', staffMemberId);

  if (error) throw error;

  const balances: BankOfHoursBalanceResponse = (data || []).map((b: any) => ({
    id: b.id,
    type: mapPtoTypeFromDb(b.type),
    availableHours: Number(b.available_hours ?? 0),
    usedHours: Number(b.used_hours ?? 0),
    scheduledHours: 0,
    staffMemberId: b.profile_id,
    unlimited: false,
    createdAt: b.created_at,
    updatedAt: b.updated_at,
  }));

  return asResponse(balances);
};

type GetPTOsAsAdminOptions = {
  page: number;
  take: number;
  filter?: PTOStatus;
  activeFromToday?: boolean;
  order?: 'ASC' | 'DESC';
};

const getPTOsAsAdmin = async ({
  take,
  page,
  filter,
  activeFromToday = false,
  order,
}: GetPTOsAsAdminOptions): Promise<AxiosResponse<ResponseWithPagination<PTORequest[]>>> => {
  let query = supabase
    .from('pto_requests')
    .select('*, pto_hours_entries(*)', { count: 'exact' });

  if (filter) {
    query = query.eq('status', mapStatusToDb(filter));
  }

  if (activeFromToday) {
    const today = new Date().toISOString().split('T')[0];
    query = query.gte('first_day', today);
  }

  const orderDirection = order === 'ASC' ? true : false;
  query = query.order('first_day', { ascending: orderDirection });

  const [from, to] = paginationRange(page, take);
  query = query.range(from, to);

  const { data, error, count } = await query;

  if (error) throw error;

  const totalCount = count ?? 0;
  const ptoRequests = (data || []).map((r: any) => parsePTORow(r));

  return asResponse<ResponseWithPagination<PTORequest[]>>(buildPaginatedResponse(ptoRequests, totalCount, page, take) as any);
};

export type UpdatePTOsAsAdminOptions = {
  id: string;
  staffMemberId: string;
  managerId?: string;
  ptoType?: PTOType;
  dateRange?: Array<Date>;
  hours?: Array<number>;
  status?: PTOStatus;
  requestComment?: string;
  decisionComment?: string;
};

const updatePTOsAsAdmin = async ({
  id,
  staffMemberId,
  managerId,
  ptoType,
  dateRange,
  hours,
  status,
  requestComment,
  decisionComment,
}: UpdatePTOsAsAdminOptions): Promise<AxiosResponse<PTORequest>> => {
  const updates: Record<string, any> = {};

  if (staffMemberId) updates.profile_id = staffMemberId;
  if (ptoType) updates.pto_type = ptoType;
  if (status) {
    updates.status = mapStatusToDb(status);
    updates.decision_date = new Date().toISOString();
  }
  if (dateRange && dateRange.length >= 2) {
    updates.first_day = dateRange[0].toISOString().split('T')[0];
  }
  if (requestComment !== undefined) updates.request_comment = requestComment;
  if (decisionComment !== undefined) updates.decision_comment = decisionComment;

  const { error: updateError } = await supabase
    .from('pto_requests')
    .update(updates)
    .eq('id', id);

  if (updateError) throw updateError;

  // If hours/dates changed, replace pto_hours_entries
  if (hours && dateRange && dateRange.length >= 2) {
    await supabase.from('pto_hours_entries').delete().eq('pto_request_id', id);
    const entries: { pto_request_id: string; date: string; hours: number }[] = [];
    const current = new Date(dateRange[0]);
    for (let i = 0; i < hours.length; i++) {
      entries.push({
        pto_request_id: id,
        date: new Date(current).toISOString().split('T')[0],
        hours: hours[i],
      });
      current.setDate(current.getDate() + 1);
    }
    if (entries.length > 0) {
      await supabase.from('pto_hours_entries').insert(entries);
    }
  }

  const { data, error } = await supabase
    .from('pto_requests')
    .select('*, pto_hours_entries(*)')
    .eq('id', id)
    .single();

  if (error) throw error;
  return asResponse(parsePTORow(data));
};

export type UpdatePTOOptions = {
  id: string;
  staffMemberId: string;
  managerId?: string;
  ptoType?: PTOType;
  dateRange?: {
    startDate: Date;
    endDate: Date;
  };
  hours?: Array<number>;
  status?: PTOStatus;
  requestComment?: string;
  decisionComment?: string;
};

const updatePTO = async ({
  id,
  staffMemberId,
  managerId,
  ptoType,
  dateRange,
  hours,
  status,
  requestComment,
  decisionComment,
}: UpdatePTOOptions): Promise<AxiosResponse<PTORequest>> => {
  const updates: Record<string, any> = {};

  if (staffMemberId) updates.profile_id = staffMemberId;
  if (ptoType) updates.pto_type = ptoType;
  if (status) {
    updates.status = mapStatusToDb(status);
    updates.decision_date = new Date().toISOString();
  }
  if (dateRange) {
    updates.first_day = dateRange.startDate.toISOString().split('T')[0];
  }
  if (requestComment !== undefined) updates.request_comment = requestComment;
  if (decisionComment !== undefined) updates.decision_comment = decisionComment;

  const { error: updateError } = await supabase
    .from('pto_requests')
    .update(updates)
    .eq('id', id);

  if (updateError) throw updateError;

  // If hours/dates changed, replace pto_hours_entries
  if (hours && dateRange) {
    await supabase.from('pto_hours_entries').delete().eq('pto_request_id', id);
    const entries: { pto_request_id: string; date: string; hours: number }[] = [];
    const current = new Date(dateRange.startDate);
    for (let i = 0; i < hours.length; i++) {
      entries.push({
        pto_request_id: id,
        date: new Date(current).toISOString().split('T')[0],
        hours: hours[i],
      });
      current.setDate(current.getDate() + 1);
    }
    if (entries.length > 0) {
      await supabase.from('pto_hours_entries').insert(entries);
    }
  }

  const { data, error } = await supabase
    .from('pto_requests')
    .select('*, pto_hours_entries(*)')
    .eq('id', id)
    .single();

  if (error) throw error;
  return asResponse(parsePTORow(data));
};

const deletePTO = async (id: string): Promise<AxiosResponse<null>> => {
  const { error } = await supabase
    .from('pto_requests')
    .delete()
    .eq('id', id);

  if (error) throw error;

  return asResponse(null);
};

const getOverlappingUpcomingPTOsByTeam = async ({
  teamId,
  ptoId,
}: {
  teamId: string;
  ptoId: string;
}): Promise<AxiosResponse<PTORequest[]>> => {
  // Get the PTO and its hour entries to find date range
  const { data: ptoData, error: ptoError } = await supabase
    .from('pto_requests')
    .select('*, pto_hours_entries(*)')
    .eq('id', ptoId)
    .single();

  if (ptoError) throw ptoError;

  const entries = ptoData.pto_hours_entries || [];
  if (entries.length === 0) return asResponse<PTORequest[]>([]);

  const dates = entries.map((e: any) => e.date).sort();
  const firstDate = dates[0];
  const lastDate = dates[dates.length - 1];

  // Get profile_ids in the same team
  const { data: teamMembers, error: teamError } = await supabase
    .from('profiles')
    .select('id')
    .eq('team_id', teamId);

  if (teamError) throw teamError;

  const memberIds = (teamMembers ?? []).map((m: any) => m.id);

  if (memberIds.length === 0) {
    return asResponse<PTORequest[]>([]);
  }

  // Find overlapping PTOs via pto_hours_entries date overlap
  const { data, error } = await supabase
    .from('pto_requests')
    .select('*, pto_hours_entries(*)')
    .in('profile_id', memberIds)
    .neq('id', ptoId)
    .lte('first_day', lastDate)
    .in('status', ['pending', 'approved']);

  if (error) throw error;

  // Filter to those that actually have overlapping dates
  const ptoRequests = (data || [])
    .filter((r: any) => {
      const rDates = (r.pto_hours_entries || []).map((e: any) => e.date);
      return rDates.some((d: string) => d >= firstDate && d <= lastDate);
    })
    .map((r: any) => parsePTORow(r));

  return asResponse(ptoRequests);
};

export default {
  createPTORequest,
  getPTOsByStaffMember,
  getBankOfHoursBalance,
  getPTOsAsAdmin,
  updatePTOsAsAdmin,
  updatePTO,
  deletePTO,
  getOverlappingUpcomingPTOsByTeam,
};
